import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Filter,
  Plus,
  Edit2,
  Trash2,
  Eye,
  MoreVertical,
  CheckCircle,
  Clock,
  BookOpen,
  Archive,
  RotateCcw,
  Share2,
  Copy,
  Link as LinkIcon,
  Copy as CopyIcon,
  UserPlus,
  UserMinus,
  LogOut,
  Loader2,
  AlertCircle,
  AlertTriangle
} from 'lucide-react';
import { useSermons } from '../hooks/useSermons';
import { usePresenceForSermons } from '../hooks/useSermonPresence';
import { useSubscription } from '../hooks/useSubscription';
import { format, parseISO, startOfDay } from 'date-fns';
import { supabase } from '../lib/supabase';
import { TeamShareModal } from './Teams/TeamShareModal';
import { SermonShareModal } from './SermonShareModal';
import { SeriesShareModal } from './SeriesShareModal';
import { WelcomeModal } from './WelcomeModal';
import { TrialBanner } from './TrialBanner';
import { SaveNotification } from './SaveNotification';
import { useTrialReminder } from '../hooks/useTrialReminder';
import { ConfirmationModal } from './ConfirmationModal';

const seriesColors = [
  { bg: 'bg-crimson/10', text: 'text-crimson' },
  { bg: 'bg-electric/10', text: 'text-electric' },
  { bg: 'bg-emerald/10', text: 'text-emerald' },
  { bg: 'bg-gold/10', text: 'text-gold' },
  { bg: 'bg-fiery/10', text: 'text-fiery' },
];

export function SermonList() {
  const navigate = useNavigate();
  const { 
    sermons, 
    series, 
    loading, 
    error, 
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    seriesFilter,
    setSeriesFilter,
    showArchived,
    setShowArchived,
    deleteSermon,
    toggleArchive,
    deleteSeries,
    stopSharing,
    removeUserSharing,
    refresh 
  } = useSermons();

  // Move all hook calls to the top
  const { showBanner, dismissBanner, daysLeftInTrial } = useTrialReminder();
  const { isCollaborator } = useSubscription();

  // Realtime presence: who is currently editing each sermon
  const allSermonIds = useMemo(() => sermons.map(s => s.id), [sermons]);
  const presenceMap = usePresenceForSermons(allSermonIds);

  const [copyingId, setCopyingId] = useState<string | null>(null);
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
  const [activeSermonMenu, setActiveSermonMenu] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [sharingSermon, setSharingSermon] = useState<{ id: string; title: string } | null>(null);
  const [shareModalData, setShareModalData] = useState<{
    sermonId: string;
    sermonTitle: string;
    sermonScripture: string;
    sermonContent: string;
    youtubeUrl: string;
    sermonDate: string;
  } | null>(null);
  const [seriesShareModalData, setSeriesShareModalData] = useState<{
    seriesId: string;
    seriesTitle: string;
    seriesDescription: string | null;
    sermons: Array<{
      id: string;
      title: string;
      scripture: string;
      content: string;
      date: string | null;
      youtube_url?: string;
    }>;
  } | null>(null);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [saveNotification, setSaveNotification] = useState({ show: false, message: '', type: 'success' as 'success' | 'error' | 'info' });
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    sermonId: string;
    sermonTitle: string;
  }>({ isOpen: false, sermonId: '', sermonTitle: '' });
  const [removeConfirmation, setRemoveConfirmation] = useState<{
    isOpen: boolean;
    sermonId: string;
    sermonTitle: string;
    isTeamShared: boolean;
  }>({ isOpen: false, sermonId: '', sermonTitle: '', isTeamShared: false });
  const [seriesDeleteConfirmation, setSeriesDeleteConfirmation] = useState<{
    isOpen: boolean;
    seriesId: string;
    seriesTitle: string;
    sermonCount: number;
  }>({ isOpen: false, seriesId: '', seriesTitle: '', sermonCount: 0 });

  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  useEffect(() => {
    if (!loading && !initialLoadComplete) {
      setInitialLoadComplete(true);
      if (sermons.length === 0 && !localStorage.getItem('welcomeModalShown')) {
        setShowWelcomeModal(true);
      }
    }
  }, [loading, initialLoadComplete]);

  // Check for mobile on mount and resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const seriesColorMap = useMemo(() => {
    const map = new Map();
    series.forEach((s, index) => {
      map.set(s.id, seriesColors[index % seriesColors.length]);
    });
    map.set('no-series', { 
      bg: 'bg-slate/10', 
      text: 'text-slate' 
    });
    map.set('shared-sermons', { 
      bg: 'bg-electric/10', 
      text: 'text-electric' 
    });
    return map;
  }, [series]);

  const sermonsBySeries = useMemo(() => {
    return sermons.reduce((acc, sermon) => {
      let seriesId;
      let seriesData;

      // Check if this is a shared sermon
      if (sermon.shared_by) {
        seriesId = 'shared-sermons';
        seriesData = {
          id: 'shared-sermons',
          title: 'Shared Sermons',
          description: 'Sermons shared with you by other users'
        };
      } else {
        // Regular sermon grouping
        seriesId = sermon.series_id || 'no-series';
        seriesData = series.find(s => s.id === seriesId) || {
          id: 'no-series',
          title: 'Uncategorized Sermons',
          description: null
        };
      }

      if (!acc[seriesId]) {
        acc[seriesId] = {
          series: seriesData,
          sermons: []
        };
      }
      acc[seriesId].sermons.push(sermon);
      return acc;
    }, {} as Record<string, { series: any; sermons: any[] }>);
  }, [sermons, series]);

  const nonEmptySermonsBySeries = useMemo(() => {
    return Object.entries(sermonsBySeries)
      .filter(([seriesId, { sermons }]) => {
        if (sermons.length === 0) return false;
        // Collaborators only see shared sermons — hide their own library (none should exist, but guard defensively)
        if (isCollaborator && seriesId !== 'shared-sermons') return false;
        return true;
      });
  }, [sermonsBySeries, isCollaborator]);

  const formatDate = (dateStr: string) => {
    const date = startOfDay(parseISO(dateStr));
    return format(date, 'MMM d, yyyy');
  };

  const handleShareSermon = async (sermonId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Fetch sermon details needed for SermonShareModal
      const { data: sermon, error: sermonFetchError } = await supabase
        .from('sermons')
        .select('id, title, scripture, content, date, youtube_url')
        .eq('id', sermonId)
        .single();

      if (sermonFetchError) throw sermonFetchError;

      setShareModalData({
        sermonId: sermon.id,
        sermonTitle: sermon.title,
        sermonScripture: sermon.scripture,
        sermonContent: sermon.content,
        youtubeUrl: sermon.youtube_url || '',
        sermonDate: sermon.date
      });

      // Close menu if open
      setActiveSermonMenu(null);
    } catch (err) {
      console.error('Error preparing sermon for sharing:', err);
      alert('Failed to prepare sermon for sharing. Please try again.');
    }
  };

  const handleDuplicateSermon = async (sermonId: string) => {
    try {
      setDuplicatingId(sermonId);

      // Get the current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) throw new Error('Not authenticated');

      // Get the sermon to duplicate
      const { data: sermon, error: fetchError } = await supabase
        .from('sermons')
        .select('*')
        .eq('id', sermonId)
        .single();

      if (fetchError) throw fetchError;

      // Create a new sermon with the same content, but owned by the current user
      const { data: newSermon, error: insertError } = await supabase
        .from('sermons')
        .insert([{
          user_id: user.id, // Use current user's ID instead of original sermon's user_id
          title: `${sermon.title} (Copy)`,
          scripture: sermon.scripture,
          content: sermon.content,
          status: sermon.status,
          series_id: null, // Don't copy series_id as it might belong to another user
          date: null, // Don't copy the date to avoid scheduling conflicts
          summary: sermon.summary,
          archived: false,
          version: 1
        }])
        .select()
        .single();

      if (insertError) throw insertError;

      // Refresh the sermon list
      await refresh();

      // Navigate to the new sermon
      navigate(`/dashboard/editor/${newSermon.id}`);

      // Close menu if open
      setActiveSermonMenu(null);
    } catch (err) {
      console.error('Error duplicating sermon:', err);
      alert('Failed to duplicate sermon. Please try again.');
    } finally {
      setDuplicatingId(null);
    }
  };

  const handleShareWithTeam = (sermon: any) => {
    setSharingSermon({ id: sermon.id, title: sermon.title });
    setActiveSermonMenu(null);
  };

  const handleStopSharing = (sermonId: string, sermonTitle: string, isTeamShared: boolean) => {
    setRemoveConfirmation({ isOpen: true, sermonId, sermonTitle, isTeamShared });
  };

  const confirmRemoveFromDashboard = async () => {
    try {
      await stopSharing(removeConfirmation.sermonId, removeConfirmation.isTeamShared);
      setSaveNotification({
        show: true,
        message: 'Sermon removed from your dashboard.',
        type: 'success'
      });
      setTimeout(() => setSaveNotification(prev => ({ ...prev, show: false })), 3000);
    } catch (error) {
      console.error('Error removing sermon from dashboard:', error);
      setSaveNotification({
        show: true,
        message: 'Failed to remove sermon. Please try again.',
        type: 'error'
      });
      setTimeout(() => setSaveNotification(prev => ({ ...prev, show: false })), 3000);
    }
  };

  const handleRemoveUserSharing = async (sermonId: string, sharedWithUserId: string) => {
    try {
      await removeUserSharing(sermonId, sharedWithUserId);
      setSaveNotification({
        show: true,
        message: 'Stopped sharing with user successfully!',
        type: 'success'
      });
      setTimeout(() => setSaveNotification(prev => ({ ...prev, show: false })), 3000);
      refresh(); // Refresh sermon list to reflect changes
    } catch (error) {
      console.error('Error stopping sharing with user:', error);
      setSaveNotification({
        show: true,
        message: 'Failed to stop sharing with user. Please try again.',
        type: 'error'
      });
      setTimeout(() => setSaveNotification(prev => ({ ...prev, show: false })), 3000);
    }
  };

  const handleDeleteSermon = (sermonId: string, sermonTitle: string) => {
    setDeleteConfirmation({
      isOpen: true,
      sermonId,
      sermonTitle
    });
  };

  const confirmDeleteSermon = async () => {
    try {
      await deleteSermon(deleteConfirmation.sermonId);
      setSaveNotification({
        show: true,
        message: 'Sermon deleted successfully!',
        type: 'success'
      });
      setTimeout(() => setSaveNotification(prev => ({ ...prev, show: false })), 3000);
    } catch (error) {
      console.error('Error deleting sermon:', error);
      setSaveNotification({
        show: true,
        message: 'Failed to delete sermon. Please try again.',
        type: 'error'
      });
      setTimeout(() => setSaveNotification(prev => ({ ...prev, show: false })), 3000);
    }
  };

  const confirmDeleteSeries = async () => {
    try {
      await deleteSeries(seriesDeleteConfirmation.seriesId);
      setSaveNotification({
        show: true,
        message: 'Series deleted successfully! Sermons have been moved to "No Series".',
        type: 'success'
      });
      setTimeout(() => setSaveNotification(prev => ({ ...prev, show: false })), 3000);
    } catch (error) {
      console.error('Error deleting series:', error);
      setSaveNotification({
        show: true,
        message: 'Failed to delete series. Please try again.',
        type: 'error'
      });
      setTimeout(() => setSaveNotification(prev => ({ ...prev, show: false })), 3000);
    }
  };

  const toggleSermonMenu = (sermonId: string) => {
    setActiveSermonMenu(activeSermonMenu === sermonId ? null : sermonId);
  };

  // Show loading state
  if (loading) {
    return (
      <div className="p-4 md:p-8">
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-electric" />
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="p-4 md:p-8">
        <div className="bg-crimson/10 text-crimson p-6 rounded-lg">
          <h3 className="text-lg font-semibold mb-2">Error Loading Sermons</h3>
          <p className="mb-4">{error}</p>
          <button 
            onClick={refresh}
            className="px-4 py-2 bg-crimson text-white rounded-lg hover:bg-crimson/90"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const handleGenerateSharePage = (sermon: any) => {
    setShareModalData({
      sermonId: sermon.id,
      sermonTitle: sermon.title,
      sermonScripture: sermon.scripture,
      sermonContent: sermon.content,
      youtubeUrl: sermon.youtube_url || '',
      sermonDate: sermon.date
    });
  };

  const handleShareSeries = async (seriesData: any, seriesSermons: any[]) => {
    try {
      // Get full sermon data including content and youtube_url
      const enrichedSermons = await Promise.all(
        seriesSermons.map(async (sermon) => {
          const { data: fullSermon, error } = await supabase
            .from('sermons')
            .select('id, title, scripture, content, date, youtube_url')
            .eq('id', sermon.id)
            .single();

          if (error) {
            console.error('Error fetching sermon details:', error);
            return {
              id: sermon.id,
              title: sermon.title,
              scripture: sermon.scripture,
              content: '',
              date: sermon.date,
              youtube_url: null
            };
          }

          return {
            id: fullSermon.id,
            title: fullSermon.title,
            scripture: fullSermon.scripture,
            content: fullSermon.content,
            date: fullSermon.date,
            youtube_url: fullSermon.youtube_url
          };
        })
      );

      setSeriesShareModalData({
        seriesId: seriesData.id,
        seriesTitle: seriesData.title,
        seriesDescription: seriesData.description,
        sermons: enrichedSermons
      });
    } catch (error) {
      console.error('Error preparing series share data:', error);
      alert('Failed to prepare series for sharing. Please try again.');
    }
  };

  const handleWelcomeModalClose = () => {
    setShowWelcomeModal(false);
    localStorage.setItem('welcomeModalShown', 'true');
  };

  const handleImportComplete = () => {
    refresh(); // Refresh the sermon list to show the imported sermon
  };

  // Remove the problematic debounced search logic that was causing hooks order issues
  // The search is already debounced in the useSermons hook


  return (
    <div className="p-4 md:p-8 pb-20 md:pb-8">
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 md:mb-8 gap-4">
        <h1 className="text-xl md:text-2xl font-bold text-midnight">
          {isCollaborator ? 'Shared Sermons' : showArchived ? 'Archived Sermons' : 'Sermon Library'}
        </h1>
        {!isCollaborator && (
          <div className="flex flex-wrap items-center gap-2 md:gap-4">
            <button
              onClick={() => setShowArchived(!showArchived)}
              className={`flex items-center px-3 py-1.5 md:px-4 md:py-2 rounded-lg text-sm ${
                showArchived
                  ? 'bg-slate/20 text-slate hover:bg-slate/30'
                  : 'bg-sand/20 text-slate hover:bg-sand/30'
              }`}
            >
              {showArchived ? (
                <>
                  <RotateCcw className="h-4 w-4 mr-1 md:mr-2" />
                  <span className="hidden xs:inline">Show Active</span>
                </>
              ) : (
                <>
                  <Archive className="h-4 w-4 mr-1 md:mr-2" />
                  <span className="hidden xs:inline">Show Archived</span>
                </>
              )}
            </button>
            {!showArchived && (
              <button
                onClick={() => navigate('/dashboard/editor')}
                className="flex items-center px-3 py-1.5 md:px-4 md:py-2 bg-electric text-white rounded-lg hover:bg-electric/90 text-sm"
              >
                <Plus className="h-4 w-4 mr-1 md:mr-2" />
                <span>New Sermon</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* Trial Banner */}
      {showBanner && (
        <TrialBanner 
          daysLeft={daysLeftInTrial} 
          onDismiss={dismissBanner}
        />
      )}

      <div className="mb-6 flex flex-col md:flex-row gap-3 md:gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate" />
          <input
            type="text"
            placeholder="Search sermons..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-white border border-sand/40 rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent text-sm shadow-sm"
          />
        </div>
        {!isCollaborator && (
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'draft' | 'complete')}
              className="px-3 py-2 bg-white border border-sand/40 rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent text-sm shadow-sm"
            >
              <option value="all">All Status</option>
              <option value="draft">Drafts</option>
              <option value="complete">Completed</option>
            </select>
            <select
              value={seriesFilter}
              onChange={(e) => setSeriesFilter(e.target.value)}
              className="px-3 py-2 bg-white border border-sand/40 rounded-lg focus:ring-2 focus:ring-electric focus:border-transparent text-sm shadow-sm"
            >
              <option value="all">All Series</option>
              {series.map((s) => (
                <option key={s.id} value={s.id}>{s.title}</option>
              ))}
              <option value="no-series">No Series</option>
            </select>
          </div>
        )}
      </div>

      {nonEmptySermonsBySeries.length === 0 ? (
        <div className="bg-white dark:bg-charcoal rounded-lg shadow-sm p-8 text-center text-slate dark:text-soft-white/80 border border-sand/20 dark:border-charcoal/40">
          {isCollaborator
            ? 'No sermons have been shared with you yet. Ask your team owner to share a sermon with your team.'
            : showArchived
              ? 'No archived sermons found'
              : 'No sermons found matching your criteria'}
        </div>
      ) : (
        <div className="space-y-6 md:space-y-12 pb-16 md:pb-0">
          {nonEmptySermonsBySeries.map(([seriesId, { series: currentSeries, sermons: seriesSermons }]) => {
            const seriesColor = seriesColorMap.get(seriesId) || { bg: 'bg-slate/10', text: 'text-slate' };
            return (
              <div key={seriesId} className="bg-white dark:bg-charcoal rounded-xl overflow-hidden border border-sand/30 dark:border-charcoal/40" style={{ boxShadow: '0 2px 8px rgba(28,43,57,0.06), 0 8px 24px rgba(28,43,57,0.04)' }}>
                <div className={`px-4 py-3 md:px-6 md:py-4 border-b ${seriesColor.bg}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <BookOpen className={`h-4 w-4 md:h-5 md:w-5 ${seriesColor.text} mr-2`} />
                      <h2 className={`text-base md:text-lg font-semibold ${seriesColor.text} truncate`}>
                        {currentSeries.title}
                      </h2>
                      {currentSeries.description && (
                        <span className="ml-2 text-xs md:text-sm text-slate/80 dark:text-soft-white/60 hidden md:inline truncate">{currentSeries.description}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className={`font-mono text-[10px] tracking-widest uppercase ${seriesColor.text}`}>
                        {seriesSermons.length} sermon{seriesSermons.length !== 1 ? 's' : ''}
                      </div>
                      {/* Series Share Button - only show for owned sermons (not shared sermons series) */}
                      {seriesId !== 'shared-sermons' && seriesId !== 'no-series' && (
                        <>
                          <button
                            onClick={() => handleShareSeries(currentSeries, seriesSermons)}
                            className={`p-1.5 ${seriesColor.text} hover:bg-white/20 rounded-lg transition-colors`}
                            title="Share Series"
                          >
                            <Share2 className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setSeriesDeleteConfirmation({
                              isOpen: true,
                              seriesId: seriesId,
                              seriesTitle: currentSeries.title,
                              sermonCount: seriesSermons.length
                            })}
                            className={`p-1.5 text-crimson/70 hover:text-crimson hover:bg-white/20 rounded-lg transition-colors`}
                            title="Delete Series"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="divide-y divide-sand/10 dark:divide-charcoal/20">
                  {seriesSermons.map((sermon, index) => (
                    <div 
                      key={sermon.id} 
                      className={`p-4 md:p-8 transition-colors duration-200 ${
                        index % 2 === 0 
                          ? 'bg-white dark:bg-charcoal' 
                          : 'bg-[#fffbf5] dark:bg-midnight'
                      }`}
                    >
                      <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                        <div className="flex-1 pr-2 md:pr-8">
                          <div className="flex items-start gap-2 mb-2 md:mb-3">
                            <h3 className="text-lg md:text-xl font-semibold text-midnight dark:text-soft-white line-clamp-2 flex-1">{sermon.title}</h3>
                          </div>
                          <div className="font-mono text-xs tracking-wide text-slate dark:text-soft-white/80 mb-2 md:mb-4 line-clamp-1">{sermon.scripture}</div>
                          <div className="flex flex-wrap items-center gap-2 md:gap-4 text-xs md:text-sm text-slate dark:text-soft-white/80">
                            {sermon.date && (
                              <span className="font-mono text-xs tracking-wide">{formatDate(sermon.date)}</span>
                            )}
                            {sermon.shared_by && (
                              <span className="text-electric">
                                Shared by {sermon.shared_by.full_name || sermon.shared_by.email}
                              </span>
                            )}
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-mono text-[10px] tracking-widest uppercase font-medium ${
                              sermon.status === 'complete'
                                ? 'bg-emerald/10 text-emerald'
                                : 'bg-gold/10 text-gold'
                            }`}>
                              {sermon.status === 'complete' ? (
                                <CheckCircle className="w-3 h-3 mr-1" />
                              ) : (
                                <Clock className="w-3 h-3 mr-1" />
                              )}
                              {sermon.status === 'complete' ? 'Complete' : 'Draft'}
                            </span>
                            {sermon.shared_by && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-electric/10 text-electric whitespace-nowrap">
                                <UserPlus className="w-3 h-3 mr-1" />
                                Shared
                              </span>
                            )}
                            {!sermon.shared_by && sermon.shared_with && sermon.shared_with.length > 0 && (
                              <div className="relative group">
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald/10 text-emerald whitespace-nowrap cursor-help">
                                  <Share2 className="w-3 h-3 mr-1" />
                                  Shared ({sermon.shared_with.length})
                                </span>
                                <div className="absolute left-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-sand/20 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                                  <div className="p-3">
                                    <div className="text-xs font-medium text-midnight mb-2">Shared with:</div>
                                    <div className="space-y-1">
                                      {sermon.shared_with.map((user, index) => (
                                        <div key={index} className="flex items-center justify-between text-xs text-slate">
                                          {user.full_name}
                                          <button
                                            onClick={() => handleRemoveUserSharing(sermon.id, user.user_id)}
                                            className="p-1 text-crimson hover:text-crimson/70 rounded-full"
                                          >
                                            <UserMinus className="h-4 w-4" />
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                            {(presenceMap[sermon.id] ?? []).length > 0 && (
                              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald">
                                <span className="relative flex h-2 w-2 shrink-0">
                                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald opacity-75" />
                                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald" />
                                </span>
                                {presenceMap[sermon.id]
                                  .map(e => e.user_name.split(' ')[0])
                                  .join(', ')}{' '}
                                {presenceMap[sermon.id].length === 1 ? 'is' : 'are'} editing
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Desktop Actions */}
                        <div className="hidden md:flex md:space-x-2">
                          {sermon.shared_by ? (
                            // Actions for shared sermons (team member)
                            <>
                              {sermon.user_permission === 'editor' && (
                                <button
                                  onClick={() => navigate(`/dashboard/editor/${sermon.id}`)}
                                  className="p-2 text-slate hover:text-electric rounded-lg"
                                  title="Edit"
                                >
                                  <Edit2 className="h-5 w-5" />
                                </button>
                              )}
                              <button
                                onClick={() => navigate(`/dashboard/preach/${sermon.id}`)}
                                className="p-2 text-slate hover:text-electric rounded-lg"
                                title="Preach Mode"
                              >
                                <Eye className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => handleDuplicateSermon(sermon.id)}
                                className="p-2 text-slate hover:text-electric rounded-lg relative"
                                title="Duplicate Sermon"
                                disabled={duplicatingId === sermon.id}
                              >
                                {duplicatingId === sermon.id ? (
                                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-electric"></div>
                                ) : (
                                  <CopyIcon className="h-5 w-5" />
                                )}
                              </button>
                              <button
                                onClick={() => handleStopSharing(sermon.id, sermon.title, !!sermon.team_name)}
                                className="p-2 text-slate hover:text-crimson rounded-lg"
                                title="Remove from my dashboard"
                              >
                                <LogOut className="h-5 w-5" />
                              </button>
                            </>
                          ) : (
                            // Actions for owned sermons
                            <>
                              <button
                                onClick={() => navigate(`/dashboard/editor/${sermon.id}`)}
                                className="p-2 text-slate dark:text-soft-white/80 hover:text-electric rounded-lg"
                                title="Edit"
                              >
                                <Edit2 className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => navigate(`/dashboard/preach/${sermon.id}`)}
                                className="p-2 text-slate dark:text-soft-white/80 hover:text-electric rounded-lg"
                                title="Preach Mode"
                              >
                                <Eye className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => handleDuplicateSermon(sermon.id)}
                                className="p-2 text-slate hover:text-electric rounded-lg relative"
                                title="Duplicate Sermon"
                                disabled={duplicatingId === sermon.id}
                              >
                                {duplicatingId === sermon.id ? (
                                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-electric"></div>
                                ) : (
                                  <CopyIcon className="h-5 w-5" />
                                )}
                              </button>
                              <button
                                onClick={() => handleShareWithTeam(sermon)}
                                className="p-2 text-slate hover:text-electric rounded-lg"
                                title="Share with Team"
                              >
                                <UserPlus className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => handleShareSermon(sermon.id)}
                                className="p-2 text-slate hover:text-electric rounded-lg"
                                title="Create/Manage Share Page"
                              >
                                <Share2 className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => toggleArchive(sermon.id, sermon.archived)}
                                className="p-2 text-slate hover:text-electric rounded-lg"
                                title={sermon.archived ? 'Unarchive' : 'Archive'}
                              >
                                {sermon.archived ? (
                                  <RotateCcw className="h-5 w-5" />
                                ) : (
                                  <Archive className="h-5 w-5" />
                                )}
                              </button>
                              <button
                                onClick={() => handleDeleteSermon(sermon.id, sermon.title)}
                                className="p-2 text-slate hover:text-crimson rounded-lg"
                                title="Delete"
                              >
                                <Trash2 className="h-5 w-5" />
                              </button>
                            </>
                          )}
                        </div>

                        {/* Mobile Actions */}
                        <div className="flex md:hidden justify-end">
                          <div className="flex space-x-2">
                            {(!sermon.shared_by || sermon.user_permission === 'editor') && (
                              <button
                                onClick={() => navigate(`/dashboard/editor/${sermon.id}`)}
                                className="p-2 text-slate hover:text-electric rounded-lg"
                                title="Edit"
                              >
                                <Edit2 className="h-5 w-5" />
                              </button>
                            )}
                            <button
                              onClick={() => navigate(`/dashboard/preach/${sermon.id}`)}
                              className="p-2 text-slate hover:text-electric rounded-lg"
                              title="Preach Mode"
                            >
                              <Eye className="h-5 w-5" />
                            </button>
                            <div className="relative">
                              <button
                                onClick={() => toggleSermonMenu(sermon.id)}
                                className="p-2 text-slate hover:text-electric rounded-lg"
                                title="More Options"
                              >
                                <MoreVertical className="h-5 w-5" />
                              </button>

                              {activeSermonMenu === sermon.id && (
                                <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-sand/20 z-50">
                                  <div className="py-1">
                                    <button
                                      onClick={() => handleDuplicateSermon(sermon.id)}
                                      className="flex items-center w-full px-4 py-2 text-sm text-midnight hover:bg-sand/10"
                                      disabled={duplicatingId === sermon.id}
                                    >
                                      {duplicatingId === sermon.id ? (
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-electric mr-2"></div>
                                      ) : (
                                        <CopyIcon className="h-4 w-4 mr-2" />
                                      )}
                                      Duplicate
                                    </button>
                                    {sermon.shared_by ? (
                                      <button
                                        onClick={() => {
                                          handleStopSharing(sermon.id, sermon.title, !!sermon.team_name);
                                          setActiveSermonMenu(null);
                                        }}
                                        className="flex items-center w-full px-4 py-2 text-sm text-crimson hover:bg-sand/10"
                                      >
                                        <LogOut className="h-4 w-4 mr-2" />
                                        Remove from Dashboard
                                      </button>
                                    ) : (
                                      <>
                                        <button
                                          onClick={() => handleShareWithTeam(sermon)}
                                          className="flex items-center w-full px-4 py-2 text-sm text-midnight hover:bg-sand/10"
                                        >
                                          <UserPlus className="h-4 w-4 mr-2" />
                                          Share with Team
                                        </button>
                                        <button
                                          onClick={() => {
                                            handleShareSermon(sermon.id);
                                            setActiveSermonMenu(null);
                                          }}
                                          className="flex items-center w-full px-4 py-2 text-sm text-midnight hover:bg-sand/10"
                                        >
                                          <Share2 className="h-4 w-4 mr-2" />
                                          Create/Manage Share Page
                                        </button>
                                        <button
                                          onClick={() => {
                                            toggleArchive(sermon.id, sermon.archived);
                                            setActiveSermonMenu(null);
                                          }}
                                          className="flex items-center w-full px-4 py-2 text-sm text-midnight hover:bg-sand/10"
                                        >
                                          {sermon.archived ? (
                                            <>
                                              <RotateCcw className="h-4 w-4 mr-2" />
                                              Unarchive
                                            </>
                                          ) : (
                                            <>
                                              <Archive className="h-4 w-4 mr-2" />
                                              Archive
                                            </>
                                          )}
                                        </button>
                                        <button
                                          onClick={() => {
                                            handleDeleteSermon(sermon.id, sermon.title);
                                            setActiveSermonMenu(null);
                                          }}
                                          className="flex items-center w-full px-4 py-2 text-sm text-crimson hover:bg-sand/10"
                                        >
                                          <Trash2 className="h-4 w-4 mr-2" />
                                          Delete
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
            {seriesShareModalData && (
        <SeriesShareModal
          seriesId={seriesShareModalData.seriesId}
          seriesTitle={seriesShareModalData.seriesTitle}
          seriesDescription={seriesShareModalData.seriesDescription}
          sermons={seriesShareModalData.sermons}
          onClose={() => setSeriesShareModalData(null)}
        />
      )}

      {sharingSermon && (
        <TeamShareModal
          sermonId={sharingSermon.id}
          sermonTitle={sharingSermon.title}
          onClose={() => {
            setSharingSermon(null);
            setSaveNotification({
              show: true,
              message: 'Team sharing updated!',
              type: 'success'
            });
            setTimeout(() => setSaveNotification(prev => ({ ...prev, show: false })), 3000);
          }}
        />
      )}
      {shareModalData && (
              <SermonShareModal
                sermonId={shareModalData.sermonId}
                sermonTitle={shareModalData.sermonTitle}
                sermonScripture={shareModalData.sermonScripture}
                sermonContent={shareModalData.sermonContent}
                youtubeUrl={shareModalData.youtubeUrl}
                sermonDate={shareModalData.sermonDate}
                onClose={() => setShareModalData(null)}
              />
            )}

      <WelcomeModal
        isOpen={showWelcomeModal}
        onClose={handleWelcomeModalClose}
        onImportComplete={handleImportComplete}
      />

      <SaveNotification
        show={saveNotification.show}
        onHide={() => setSaveNotification({ ...saveNotification, show: false })}
        message={saveNotification.message}
        type={saveNotification.type}
      />

      <ConfirmationModal
        isOpen={deleteConfirmation.isOpen}
        onClose={() => setDeleteConfirmation({ isOpen: false, sermonId: '', sermonTitle: '' })}
        onConfirm={confirmDeleteSermon}
        title="Delete Sermon"
        message={`Are you sure you want to delete "${deleteConfirmation.sermonTitle}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonClass="bg-crimson text-white hover:bg-crimson/90"
        icon={<AlertTriangle className="h-5 w-5 text-crimson" />}
      />

      <ConfirmationModal
        isOpen={removeConfirmation.isOpen}
        onClose={() => setRemoveConfirmation({ isOpen: false, sermonId: '', sermonTitle: '', isTeamShared: false })}
        onConfirm={confirmRemoveFromDashboard}
        title="Remove from Dashboard"
        message={`Remove "${removeConfirmation.sermonTitle}" from your dashboard? The sermon will stay in the owner's library and other team members won't be affected.`}
        confirmText="Remove"
        cancelText="Cancel"
        confirmButtonClass="bg-slate text-white hover:bg-midnight"
        icon={<LogOut className="h-5 w-5 text-slate" />}
      />

      <ConfirmationModal
        isOpen={seriesDeleteConfirmation.isOpen}
        onClose={() => setSeriesDeleteConfirmation({ isOpen: false, seriesId: '', seriesTitle: '', sermonCount: 0 })}
        onConfirm={confirmDeleteSeries}
        title="Delete Series"
        message={`Are you sure you want to delete the series "${seriesDeleteConfirmation.seriesTitle}"?${seriesDeleteConfirmation.sermonCount > 0 ? ` The ${seriesDeleteConfirmation.sermonCount} sermon${seriesDeleteConfirmation.sermonCount !== 1 ? 's' : ''} in this series will not be deleted — they will be moved to "No Series".` : ''} This action cannot be undone.`}
        confirmText="Delete Series"
        cancelText="Cancel"
        confirmButtonClass="bg-crimson text-white hover:bg-crimson/90"
        icon={<AlertTriangle className="h-5 w-5 text-crimson" />}
      />
    </div>
  );
}